import { faro } from '@grafana/faro-web-sdk';

/**
 * One Faro session per visit. We never rotate the session ID — only update
 * its attributes — so a single session covers pre-login → authenticated
 * → precheck → test → end. User identity rides on Faro's user meta and is
 * also echoed into session.attributes for direct lookup.
 */

export interface FaroUserInfo {
    /** Stable per-candidate identity. Cognito sub on form login, Inspera UserID in iframe mode. */
    id: string;
    email?: string;
    username?: string;
}

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/** Custom session ID format: `YYYYMMDD-HHmm-<rand>` (local time). */
export function generateSessionId(): string {
    const d = new Date();
    const date = `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
    const time = `${pad2(d.getHours())}${pad2(d.getMinutes())}`;
    const rand =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID().split('-')[0]
            : Math.random().toString(36).slice(2, 10);
    return `${date}-${time}-${rand}`;
}

/** Attach user identity (Faro user meta) and echo it into session.attributes. */
export function setFaroUser(user: FaroUserInfo): void {
    if (!faro?.api) return;
    faro.api.setUser({
        id: user.id,
        username: user.username ?? user.email,
        email: user.email,
    });
    setFaroSession({
        userId: user.id,
        ...(user.email ? { userEmail: user.email } : {}),
        ...(user.username ? { userName: user.username } : {}),
    });
}

/** Merge attributes into the current session (e.g. stage transitions). */
export function setFaroSession(attrs: Record<string, string>): void {
    if (!faro?.api) return;
    const current = faro.api.getSession();
    faro.api.setSession({
        id: current?.id,
        attributes: { ...current?.attributes, ...attrs },
    });
}

/** Clear user identity and reset session attributes to `{ stage: 'pre-login' }`. */
export function resetFaroIdentity(): void {
    if (!faro?.api) return;
    faro.api.resetUser();
    const current = faro.api.getSession();
    faro.api.setSession({ id: current?.id, attributes: { stage: 'pre-login' } });
}

/** 
 * Emit a named custom event (silence_check_*, speak_check_*, etc.). 
 * 
 * Events are always sent to Grafana Faro for monitoring and analytics.
 */
export function pushFaroEvent(name: string, attrs?: Record<string, string>): void {
    if (!faro?.api) return;
    faro.api.pushEvent(name, attrs);
}

if (process.env.NODE_ENV !== 'production') {
    (window as unknown as Record<string, unknown>).__faroSession = {
        setFaroUser,
        setFaroSession,
        resetFaroIdentity,
        pushFaroEvent,
    };
}
